import { useState } from "react";
import {
	Box,
	Button,
	Divider,
	Drawer,
	DrawerBody,
	DrawerCloseButton,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerOverlay,
	Flex,
	Icon,
	Stack,
	Text,
	useToast,
	useBreakpointValue
} from "@chakra-ui/react";
import { FiCornerDownRight, FiShoppingBag } from "react-icons/fi";

import api from "../../services/api";
import { useCart } from "../../hooks/cart";
import AlertCancelPedido from "../ComponentsProducao/AlertCancelPedido";
import ModalProdutoItem from '../ComponentsProducao/ModalProdutoItem';

type ProdutoData = {
	id: string;
	categoria: string;
	cod_barras: string;
	cod_pro: string;
	comiss_ind: number;
	comiss_ven: number;
	complementos: ComplementoData[];
	descricao: string;
	nome: string;
	obs: string;
	promocao: string;
	qtd: number;
	url_imagem: string;
	valor: number;
	valorFormatted: string;
	total: number;
	totalFormatted: string;
}
type ComplementoData = {
	codigo: number;
	grupo: number;
	nome: string;
	descricao: string;
	custo: number;
	valor: number;
	priceFormatted: string;
	qtd_maxima: number;
	qtd_gratis: number;
	qtd: number;
}
interface CartProps {
	isOpen: boolean;
	onRequestClose: (close: boolean) => void;
}

const Cart: React.FC<CartProps> = ({ 
	isOpen,
	onRequestClose,
}) => {
	const isWideVersion = useBreakpointValue({
		base: false,
		lg: true,
	});

	const { cart, initCart, removeCart } = useCart();
	const toast = useToast();

	const [isOpenAlertCancelPedido, setIsOpenAlertCancelPedido] = useState(false);

	const [isOpenModalItem, setIsOpenModalItem] = useState(false);
	const [produtoItemModal, setProdutoItemModal] = useState<ProdutoData>({} as ProdutoData);

	function handleRemoveCart(id: string) {
		removeCart(id);

		if (!cart || !cart.items || cart.items?.length === 0) {
			onRequestClose(false);

			toast({
				title: 'Carrinho.',
				description: 'Você excluiu todos os itens do carrinho!',
				status: 'info',
				duration: 5000,
				position: 'top-right',
				isClosable: true,
				containerStyle: {
					marginTop: '20'
				}
			})
		}
	}

	async function handleSubmitPedido() {
		try {
			await api.post('/controle/pedido', { pedido: cart });

			toast({
				title: 'Pedido feito.',
				description: 'Seu pedido foi feito com sucesso!',
				status: 'success',
				duration: 5000,
				position: 'top-right',
				isClosable: true,
			});

			// setIsOpenModalConferencia(false);
			initCart();
			onRequestClose(false);

		} catch (error) {
			console.log('Error handleSubmitPedido ==> ', error);
			toast({
				title: 'Pedido falhou.',
				description: `Seu pedido falhou ao finalizar! - ${error}`,
				status: 'error',
				duration: 5000,
				position: 'top-right',
				isClosable: true,
			});
		}
	};

	return (
		<Drawer
			isOpen={isOpen}
			placement="right"
			onClose={() => onRequestClose(false)}
			size={["full", "full", "md", "md", "md"]}
			blockScrollOnMount={false}
		>
			<DrawerOverlay />

			<DrawerContent mt={["0", "0", "3.7rem", "3.5rem", "3.5rem"]}>
				<DrawerCloseButton /> {/* "3.8rem", "3.7rem", "3rem", "3.5rem", "3.1rem"]}>*/}

				{cart && cart.cliente && (
					<>
						<DrawerHeader pt="2" pb="1">
							<Flex direction="column">
								<Text fontSize="18px" fontWeight="500" color="gray.600">
									{cart?.cliente.nome}
								</Text>
							</Flex>
						</DrawerHeader>

						<Box px="4">
							<Divider orientation='horizontal' size="sm" />
						</Box>
					</>
				)}

				{!cart || cart.items?.length === 0 ? (
					<DrawerBody>
						<Flex
							w="100%"
							h="100%"
							direction="column"
							justify="center"
							align="center"
						>
							<Flex p="8" bg="gray.100" borderRadius="full">
								<Icon as={FiShoppingBag} fontSize="80" />
							</Flex>
							<Text pt="6" fontSize="24">Sua sacola está vazia!</Text>
							<Text pt="2" fontSize="20">Adicione itens em sua sacola.</Text>
						</Flex>
					</DrawerBody>
				) : (
					<DrawerBody>
						{cart.items?.map((item) => {
							return (
								<Flex
									key={item.id}
									flexDir="column"
									py="2"
									borderBottomWidth={1}
									borderBottomColor="gray.100"
								>
									<Flex justify="space-between">
										<Text fontSize="15px">{item.qtd}x {item.nome}</Text>
										<Text fontSize="15px">{item.totalFormatted}</Text>
									</Flex>

									{item.complementos?.map(comp => {
										return comp.qtd > 0 && (
											<Flex key={comp.codigo} px="2">
												<Icon as={FiCornerDownRight} color="gray.300" />
												<Text fontSize="14px">{comp.qtd}x {comp.nome}</Text>
											</Flex>
										)
									})}

									<Stack spacing="2" direction="row">
										<Text
											as="button"
											color="blue.500"
											fontSize="14px"
											fontWeight="500"
											onClick={() => {
												// setIsAddNewItem(false);
												setProdutoItemModal(item);
												setIsOpenModalItem(true);
											}}
										>
											Editar
										</Text>
										<Text
											as="button"
											color="gray.500"
											fontSize="14px"
											fontWeight="500"
											onClick={() => handleRemoveCart(item.id)}
										>
											Remover
										</Text>
									</Stack>
								</Flex>
							)
						})}

						<Flex direction="column" pt="4">
							<Flex justify="space-between">
								<Text fontSize="15" fontWeight="500" color="gray.400">SubTotal</Text>
								<Text fontSize="15" fontWeight="500" color="gray.400">{cart?.subTotalFormatted}</Text>
							</Flex>

							<Flex justify="space-between">
								<Text fontSize="15" fontWeight="500" color="gray.800">Total</Text>
								<Text fontSize="15" fontWeight="500" color="gray.800">{cart?.totalFormatted}</Text>
							</Flex>
						</Flex>
					</DrawerBody>
				)}

				<DrawerFooter w="100%">
					{cart?.items?.length > 0 &&
						<Stack direction="row" spacing="2" w="100%">
							<Button
								w="100%"
								colorScheme='blue'
								fontSize={["12", "16"]}
								isDisabled={cart?.items?.length === 0}
								onClick={handleSubmitPedido}
							>
								Enviar Produção
							</Button>

							<Button
								w="100%"
								colorScheme="blackAlpha"
								fontSize={["12", "16"]}
								onClick={() => setIsOpenAlertCancelPedido(true)}
							>
								Cancelar Pedido
							</Button>
						</Stack>
					}
				</DrawerFooter>
			</DrawerContent>

			<AlertCancelPedido
				isOpen={isOpenAlertCancelPedido}
				isCloseCart={onRequestClose}
				onRequestClose={setIsOpenAlertCancelPedido}
			/>

			{isOpenModalItem && (
				<ModalProdutoItem
					isOpen={isOpenModalItem}
					onRequestClose={setIsOpenModalItem}
					produto={produtoItemModal}
					incluir={false}
				/>
			)}
		</Drawer>
	)
}

export default Cart;